// AIImageGenerator.js — v3 Template Engine Edition
// Flow:
//   1. Content Details  — headline, subtext, CTA (pre-filled from post brief)
//   2. Design Template  — pick 1 of 6 brand-aware poster templates (thumbnail preview)
//   3. Background Style — photographic / illustration / minimal / bold + aspect ratio
//   4. AI Prompt        — auto-engineered from business data, editable, regeneratable
//   5. Generate         — AI creates background scene
//   6. Template Engine  — code applies exact brand colours + fonts + logo + layout
//   7. Export           — single ready-to-publish PNG → Download or Apply to Post

import { useState, useEffect, useCallback } from 'react';
import { businessAPI } from '../../../services/businessAPI';
import { TEMPLATES, renderTemplate, renderThumbnail } from './PosterTemplateEngine';

// ─── Constants ────────────────────────────────────────────────────────────────

const PLATFORM_ASPECT = {
  instagram: 'square', facebook: 'square', linkedin: 'landscape',
  twitter: 'landscape', tiktok: 'story', youtube: 'landscape',
  email: 'landscape', googleBusiness: 'square',
};

const ASPECT_LABELS = {
  square: '1:1 Square', portrait: '4:5 Portrait',
  story: '9:16 Story', landscape: '16:9 Landscape',
};

const STYLE_OPTIONS = [
  { id: 'photographic', label: 'Photo',       icon: 'fas fa-camera',    gradient: 'from-blue-500 to-cyan-400',    desc: 'Ultra-realistic commercial photo' },
  { id: 'illustration', label: 'Illustration', icon: 'fas fa-pen-nib',   gradient: 'from-violet-500 to-purple-400', desc: 'Clean digital illustration' },
  { id: 'minimal',      label: 'Minimal',      icon: 'fas fa-expand',    gradient: 'from-slate-500 to-slate-400',   desc: 'Clean, spacious, zen aesthetic' },
  { id: 'bold',         label: 'Bold',         icon: 'fas fa-bolt',      gradient: 'from-orange-500 to-amber-400',  desc: 'High-contrast, striking design' },
];

const CTA_PRESETS = [
  'Book Now', 'Learn More', 'Shop Now', 'Get Started',
  'Contact Us', 'Download Free', 'Sign Up Today', 'Discover More',
];


// ─── Canvas Helper Functions ──────────────────────────────────────────────────

// Fetch any URL (incl. external AI image URLs) as a data URL for taint-free canvas use
async function fetchAsDataUrl(url) {
  if (!url) throw new Error('No image URL');
  if (url.startsWith('data:')) return url;
  const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const fullUrl = url.startsWith('http') ? url : `${API}${url}`;
  try {
    const res = await fetch(fullUrl, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth || 1024;
        c.height = img.naturalHeight || 1024;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve(c.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = fullUrl;
    });
  }
}

// Remove white/light background from logo using canvas pixel manipulation
async function removeLogoBackground(dataUrl, tolerance = 38) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = img.naturalWidth  || img.width  || 200;
      canvas.height = img.naturalHeight || img.height || 200;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      // Sample 4 corners to determine background colour
      const corners = [
        [0, 0], [canvas.width - 1, 0],
        [0, canvas.height - 1], [canvas.width - 1, canvas.height - 1],
      ];
      let sumR = 0, sumG = 0, sumB = 0;
      for (const [cx, cy] of corners) {
        const idx = (cy * canvas.width + cx) * 4;
        sumR += data[idx]; sumG += data[idx + 1]; sumB += data[idx + 2];
      }
      const bgR = Math.round(sumR / 4), bgG = Math.round(sumG / 4), bgB = Math.round(sumB / 4);
      for (let i = 0; i < data.length; i += 4) {
        const dist = Math.sqrt(
          Math.pow(data[i] - bgR, 2) + Math.pow(data[i + 1] - bgG, 2) + Math.pow(data[i + 2] - bgB, 2)
        );
        if (dist < tolerance) data[i + 3] = 0;
      }
      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

// ─── Seed Prompt Builder (instant client-side, shown while AI loads) ──────────

function buildSeedPrompt(day, businessData, style, templateId) {
  const textPosition = TEMPLATES.find(t => t.id === templateId)?.textPosition || 'bottom';
  const title    = day?.postTitle        || 'Social Media Post';
  const platform = day?.platform         || 'instagram';
  const cType    = day?.contentType      || 'promotional';
  const bizName  = businessData?.businessName || 'the business';
  const industry = businessData?.businessType || businessData?.industry || 'professional services';
  const color    = businessData?.brandDna?.primaryColour || '#6366F1';
  const moods    = { promotional: 'exciting and aspirational', educational: 'calm and authoritative', inspirational: 'uplifting and warm', entertainment: 'vibrant and fun' };
  const plats    = { instagram: 'Instagram-worthy, highly aesthetic', linkedin: 'polished and professional', facebook: 'warm and relatable', tiktok: 'bold and trend-driven' };
  const zoneMap  = { bottom: 'lower 40%', top: 'upper 40%', center: 'centre 40%' };
  return `A stunning ${style || 'photographic'} image for a ${moods[cType] || 'engaging'} ${platform} post by ${bizName} (${industry}). ${plats[platform] || 'Professional and visually appealing'}. Scene representing "${title}". Rich ${color} colour tones. Leave the ${zoneMap[textPosition] || 'lower 40%'} as a clean gradient area for text overlay. Hyperrealistic, 8K, commercial quality. No text, no words, no watermarks.`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function AIImageGenerator({
  day,
  businessData,
  onBack,
  onApplyToPost,       // (compositeDataUrl) → set imageUrl + go to preview
  onUseInDesignStudio, // (compositeDataUrl) → open design studio with composite as base
}) {
  const primaryColour   = businessData?.brandDna?.primaryColour   || '#7C3AED';
  const secondaryColour = businessData?.brandDna?.secondaryColour || '#1E40AF';
  const accentColour    = businessData?.brandDna?.accentColour    || '#F59E0B';
  const fontStyle       = businessData?.brandDna?.fontStyle       || 'modern_clean';

  const brandColours = { primary: primaryColour, secondary: secondaryColour, accent: accentColour };

  // ── Form state ────────────────────────────────────────────────────────────
  const [headline,        setHeadline]        = useState(day?.postTitle || '');
  const [subtext,         setSubtext]         = useState('');
  const [ctaText,         setCtaText]         = useState('Learn More');
  const [customCta,       setCustomCta]       = useState(false);
  const [styleChoice,     setStyleChoice]     = useState('photographic');
  const [aspectRatio,     setAspectRatio]     = useState(PLATFORM_ASPECT[day?.platform] || 'square');
  const [selectedTemplate, setSelectedTemplate] = useState('bottom_band');

  // ── Template thumbnails ────────────────────────────────────────────────────
  const [thumbnails, setThumbnails] = useState({});

  // ── Prompt state ──────────────────────────────────────────────────────────
  const [prompt,           setPrompt]           = useState('');
  const [isLoadingPrompt,  setIsLoadingPrompt]  = useState(true);
  const [promptEnhanced,   setPromptEnhanced]   = useState(false);
  const [editingPrompt,    setEditingPrompt]     = useState(false);
  const [promptError,      setPromptError]       = useState('');

  // ── Exemplar style reference (from Business DNA) ──────────────────────────
  const [selectedExemplar, setSelectedExemplar] = useState(null);
  const [viewingExemplar, setViewingExemplar] = useState(null); // url of the image in lightbox
  const exemplarImages = (businessData?.brandDna?.styleExamples || []).filter(Boolean);
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    return `${API_URL}${url}`;
  };

  // ── Generation state ──────────────────────────────────────────────────────
  const SESSION_KEY = `aiGen_day${day?.day || 0}_${day?.platform || 'x'}`;

  const [step,          setStep]          = useState(() => {
    // Restore result from sessionStorage if available (survives tab switches)
    try { return sessionStorage.getItem(SESSION_KEY) ? 'result' : 'form'; } catch { return 'form'; }
  });
  const [bgImageUrl,    setBgImageUrl]    = useState('');
  const [compositeUrl,  setCompositeUrl]  = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY) || ''; } catch { return ''; }
  });
  const [isCompositing, setIsCompositing] = useState(false);
  const [error,         setError]         = useState('');

  // Persist generated image to sessionStorage so tab-switching doesn't lose it
  const saveResult = (url) => {
    setCompositeUrl(url);
    try { sessionStorage.setItem(SESSION_KEY, url); } catch {}
  };
  const clearResult = () => {
    setCompositeUrl('');
    setStep('form');
    try { sessionStorage.removeItem(SESSION_KEY); } catch {}
  };

  // ── Pre-fill subtext from stored brief/content (no artificial truncation) ──
  useEffect(() => {
    const brief = day?.brief;
    if (brief?.keyMessages?.[0])    setSubtext(brief.keyMessages[0]);
    else if (day?.caption)          setSubtext(day.caption);
    else if (day?.generatedContent) setSubtext(day.generatedContent.slice(0, 300));
  }, [day]);

  // ── Render template thumbnails progressively ───────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const tmpl of TEMPLATES) {
        if (cancelled) break;
        try {
          const dataUrl = await renderThumbnail(tmpl.id, brandColours, fontStyle, 320);
          if (!cancelled) setThumbnails(prev => ({ ...prev, [tmpl.id]: dataUrl }));
        } catch {
          // Skip failed thumbnail silently
        }
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load AI-enhanced prompt ────────────────────────────────────────────────
  const loadPrompt = useCallback(async () => {
    const templateTextPosition = TEMPLATES.find(t => t.id === selectedTemplate)?.textPosition || 'bottom';
    setIsLoadingPrompt(true);
    setPromptEnhanced(false);
    setPromptError('');
    setPrompt(buildSeedPrompt(day, businessData, styleChoice, selectedTemplate));
    try {
      const dna = businessData?.brandDna || {};
      const result = await businessAPI.generateImagePrompt({
        dayData: {
          postTitle:   day?.postTitle   || '',
          platform:    day?.platform    || 'instagram',
          contentType: day?.contentType || 'promotional',
          pillar:      day?.pillar      || '',
        },
        businessData: {
          businessName: businessData?.businessName || '',
          businessType: businessData?.businessType || businessData?.industry || '',
          logoUrl:      businessData?.logoUrl      || dna.logoUrl || '',
          brandDna: {
            primaryColour:     dna.primaryColour      || '',
            secondaryColour:   dna.secondaryColour    || '',
            accentColour:      dna.accentColour       || '',
            visualTone:        dna.visualTone         || '',
            brandFeeling:      dna.brandFeeling       || '',
            contentPreference: dna.contentPreference  || '',
            businessSpecialty: dna.businessSpecialty  || '',
          },
        },
        style:        styleChoice,
        textPosition: templateTextPosition,
        ...(selectedExemplar ? { exemplarImageUrl: resolveUrl(selectedExemplar) } : {}),
      });
      if (result?.success && result?.prompt) {
        setPrompt(result.prompt);
        setPromptEnhanced(true);
      }
    } catch {
      setPromptError('Could not enhance prompt with AI — using smart template instead.');
    } finally {
      setIsLoadingPrompt(false);
    }
  }, [day, businessData, styleChoice, selectedTemplate, selectedExemplar]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load on mount only
  useEffect(() => { loadPrompt(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Generate ───────────────────────────────────────────────────────────────
  // Route A: AI background → Template Engine (brand colours, fonts, logo, CTA)
  // Route B: AI background styled after exemplar → raw output (no template overlay)
  const handleGenerate = async () => {
    if (!headline.trim() || !prompt.trim()) return;
    setStep('generating');
    setError('');
    setBgImageUrl('');
    saveResult('');
    setIsCompositing(false);

    try {
      // Step 1: Generate AI image
      // Route A: background scene only (template composited on top)
      // Route B: complete designed post with text embedded, style matched to exemplar
      const result = await businessAPI.generateImageVariations({
        prompt:       prompt.trim(),
        platform:     day?.platform    || 'instagram',
        contentType:  day?.contentType || 'promotional',
        style:        styleChoice,
        postTitle:    day?.postTitle   || '',
        brandColours: { primary: primaryColour, secondary: secondaryColour, accent: accentColour },
        brandDna:     businessData?.brandDna || {},
        count: 1,
        ...(selectedExemplar ? {
          exemplarImageUrl: resolveUrl(selectedExemplar),
          // Pass text content so server can embed it in the generated image
          headline: headline.trim(),
          subtext:  subtext.trim(),
          ctaText:  ctaText.trim(),
        } : {}),
      });

      if (!result?.success || !result?.variations?.length) {
        throw new Error('Image generation returned no results. Please try again.');
      }

      const rawUrl = result.variations[0]?.url || result.variations[0];
      setBgImageUrl(rawUrl);

      // Step 2: Fetch the AI background as a data URL
      const bgDataUrl = await fetchAsDataUrl(rawUrl);

      if (selectedExemplar) {
        // ── ROUTE B ────────────────────────────────────────────────────────
        // Complete designed poster — text embedded, exemplar aesthetic matched
        saveResult(bgDataUrl);
        setStep('result');
      } else {
        // ── ROUTE A ────────────────────────────────────────────────────────
        // Fetch logo, remove background, then composite with Template Engine
        let logoDataUrl = null;
        const logoUrl = businessData?.brandDna?.logoUrl || businessData?.logoUrl;
        if (logoUrl) {
          try {
            const rawLogoDataUrl = await fetchAsDataUrl(logoUrl);
            logoDataUrl = await removeLogoBackground(rawLogoDataUrl);
          } catch {
            // Continue without logo if it fails
          }
        }

        setIsCompositing(true);
        const composite = await renderTemplate(selectedTemplate, {
          bgDataUrl,
          logoDataUrl,
          headline: headline.trim(),
          subtext:  subtext.trim(),
          ctaText:  ctaText.trim(),
          aspectRatio,
          brandColours,
          fontStyle,
        });

        saveResult(composite);
        setStep('result');
      }
    } catch (err) {
      setError(err?.message || 'Image generation failed. Please try again.');
      setStep('form');
    } finally {
      setIsCompositing(false);
    }
  };

  // ── Download handler ───────────────────────────────────────────────────────
  const handleDownload = () => {
    const name = `${(day?.postTitle || 'post').replace(/\s+/g, '-').toLowerCase()}-day${day?.day || 1}.png`;
    const link = document.createElement('a');
    link.href = compositeUrl; link.download = name;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full bg-white overflow-y-auto">

      {/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <div className="flex-shrink-0 sticky top-0 z-10 flex items-center gap-3 px-5 py-3.5 border-b border-slate-100 bg-white/95 backdrop-blur-sm">
        <button
          onClick={step === 'result' ? clearResult : onBack}
          className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
        >
          <i className="fas fa-arrow-left text-sm" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
            <i className="fas fa-wand-magic-sparkles text-white text-[10px]" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900 leading-tight">AI Image Creator</h2>
            <p className="text-[10px] text-slate-400 leading-tight">Day {day?.day} · {day?.platform} · {day?.contentType}</p>
          </div>
        </div>
        <div className="flex-1" />
        {step === 'form'       && <span className="px-2.5 py-1 bg-violet-50 text-violet-600 text-[10px] font-semibold rounded-full">Setup</span>}
        {step === 'generating' && <span className="px-2.5 py-1 bg-amber-50 text-amber-600 text-[10px] font-semibold rounded-full flex items-center gap-1"><i className="fas fa-spinner fa-spin text-[8px]" />Generating</span>}
        {step === 'result'     && <span className="px-2.5 py-1 bg-green-50 text-green-700 text-[10px] font-bold rounded-full flex items-center gap-1"><i className="fas fa-circle-check text-[9px]" />Ready to publish</span>}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          STEP: GENERATING
         ══════════════════════════════════════════════════════════════════════ */}
      {step === 'generating' && (
        <div className="flex-1 flex flex-col items-center justify-center p-10 gap-8">
          <div className="relative">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
              <i className="fas fa-wand-magic-sparkles text-violet-500 text-2xl" />
            </div>
            <div className="absolute -inset-2 rounded-2xl border-2 border-violet-300/50 animate-ping" />
          </div>

          <div className="text-center">
            <p className="text-base font-bold text-slate-800">
              {selectedExemplar
                ? 'AI is analysing your style reference…'
                : isCompositing ? 'Applying template & brand colours…' : 'AI is generating your background scene…'}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {selectedExemplar
                ? 'GPT-4 Vision reads your exemplar → image generator recreates the aesthetic'
                : isCompositing
                  ? 'Template engine compositing exact brand colours, fonts, logo & CTA'
                  : 'Crafting the perfect scene from your brief — 20–45 seconds'}
            </p>
          </div>

          <div className="flex flex-col gap-3 max-w-xs w-full">
            {(selectedExemplar ? [
              { label: 'GPT-4 Vision analysing exemplar image style',        done: true },
              { label: 'Style description injected into image prompt',        done: true },
              { label: 'AI generating scene that matches the visual aesthetic', done: !!bgImageUrl },
              { label: 'Image ready — raw output, no template overlay',       done: false },
            ] : [
              { label: 'Specialty-aware prompt engineered from your brief',   done: true },
              { label: 'AI generating background scene',                      done: !!bgImageUrl || isCompositing },
              { label: 'Logo background removed',                             done: isCompositing },
              { label: 'Template engine applying brand colours, fonts & CTA', done: false },
            ]).map((s, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                  s.done ? 'bg-green-100' : selectedExemplar ? 'bg-amber-50 border border-amber-200' : 'bg-violet-50 border border-violet-200'
                }`}>
                  {s.done
                    ? <i className="fas fa-check text-green-600 text-[8px]" />
                    : <div className={`w-2 h-2 rounded-full animate-pulse ${selectedExemplar ? 'bg-amber-400' : 'bg-violet-400'}`} />
                  }
                </div>
                <span className={`text-[11px] ${s.done ? 'text-slate-500' : selectedExemplar ? 'text-amber-700 font-semibold' : 'text-violet-700 font-semibold'}`}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STEP: RESULT
         ══════════════════════════════════════════════════════════════════════ */}
      {step === 'result' && (
        <div className="flex-1 p-5 max-w-2xl mx-auto w-full space-y-5">

          <div className="text-center">
            <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold mb-2 ${
              selectedExemplar ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'
            }`}>
              <i className={`text-[9px] ${selectedExemplar ? 'fas fa-image' : 'fas fa-circle-check'}`} />
              {selectedExemplar ? 'Route B — Style Reference' : 'Ready to Publish'}
            </div>
            <p className="text-sm font-bold text-slate-800">Your image is complete</p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {selectedExemplar
                ? 'Visual aesthetic recreated from your style reference · ' + ASPECT_LABELS[aspectRatio]
                : `${TEMPLATES.find(t => t.id === selectedTemplate)?.name} template · exact brand colours applied · ${ASPECT_LABELS[aspectRatio]}`}
            </p>
          </div>

          <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-slate-200/80 bg-slate-100">
            <img src={compositeUrl} alt="Final post image" className="w-full h-auto object-contain" />
            <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1 bg-black/50 backdrop-blur-sm text-white text-[9px] font-bold rounded-full">
              <i className="fas fa-star text-amber-400 text-[8px]" />
              1080px · {ASPECT_LABELS[aspectRatio]} · Ready to publish
            </div>
          </div>

          {/* AI-generated notice */}
          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200/70 rounded-xl">
            <i className="fas fa-wand-magic-sparkles text-amber-500 text-[11px] mt-0.5 flex-shrink-0" />
            <p className="text-[10px] text-amber-800 leading-relaxed">
              <strong>AI Generated image</strong> — text and layout are approximate. Feel free to{' '}
              <button onClick={() => onUseInDesignStudio && onUseInDesignStudio(compositeUrl)} className="underline font-semibold hover:text-amber-900">
                crop or refine in Design Studio
              </button>{' '}
              before publishing.
            </p>
          </div>

          {/* Summary chips */}
          <div className="flex flex-wrap gap-2">
            {headline && (
              <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-full">
                <i className="fas fa-heading text-violet-500 text-[8px]" />
                {headline.length > 28 ? `${headline.slice(0, 28)}…` : headline}
              </span>
            )}
            {subtext && (
              <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-full">
                <i className="fas fa-align-left text-violet-500 text-[8px]" />Supporting text added
              </span>
            )}
            {ctaText && (
              <span className="flex items-center gap-1 px-2.5 py-1 bg-slate-100 text-slate-600 text-[10px] font-medium rounded-full">
                <i className="fas fa-cursor text-violet-500 text-[8px]" />CTA: {ctaText}
              </span>
            )}
            {selectedExemplar ? (
              <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-50 text-amber-700 text-[10px] font-medium rounded-full">
                <i className="fas fa-image text-amber-500 text-[8px]" />
                Route B · Style Reference match
              </span>
            ) : (
              <span className="flex items-center gap-1 px-2.5 py-1 bg-violet-50 text-violet-600 text-[10px] font-medium rounded-full">
                <i className="fas fa-palette text-violet-500 text-[8px]" />
                {TEMPLATES.find(t => t.id === selectedTemplate)?.name} template
              </span>
            )}
          </div>

          {/* Primary actions */}
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleDownload}
              className="py-3.5 px-4 border-2 border-slate-800 text-slate-800 hover:bg-slate-900 hover:text-white text-[12px] font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
            >
              <i className="fas fa-download text-[11px]" />Download PNG
            </button>
            <button
              onClick={() => onApplyToPost(compositeUrl)}
              className="py-3.5 px-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-[12px] font-bold rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-200/60"
            >
              <i className="fas fa-circle-check text-[11px]" />Apply to Post
            </button>
          </div>

          {/* Secondary actions */}
          <div className="flex items-center justify-center gap-4 pt-1">
            <button
              onClick={clearResult}
              className="text-[11px] text-slate-400 hover:text-slate-700 transition-colors flex items-center gap-1"
            >
              <i className="fas fa-pencil text-[9px]" />Edit details & regenerate
            </button>
            <span className="text-slate-200 text-[10px]">|</span>
            <button
              onClick={handleGenerate}
              className="text-[11px] text-violet-500 hover:text-violet-700 transition-colors flex items-center gap-1"
            >
              <i className="fas fa-rotate text-[9px]" />New image, same settings
            </button>
            {onUseInDesignStudio && (
              <>
                <span className="text-slate-200 text-[10px]">|</span>
                <button
                  onClick={() => onUseInDesignStudio(compositeUrl)}
                  className="text-[11px] text-slate-400 hover:text-slate-700 transition-colors flex items-center gap-1"
                >
                  <i className="fas fa-paint-brush text-[9px]" />Refine in Design Studio
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STEP: FORM
         ══════════════════════════════════════════════════════════════════════ */}
      {step === 'form' && (
        <div className="flex-1 p-5 max-w-3xl mx-auto w-full space-y-8">

          {/* ══ ROUTE A HEADER ════════════════════════════════════════════════ */}
          <div className={`flex items-center gap-3 transition-opacity ${selectedExemplar ? 'opacity-40 pointer-events-none' : ''}`}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${selectedExemplar ? 'bg-slate-300 text-slate-500' : 'bg-violet-600 text-white'}`}>
              <i className="fas fa-sliders text-[11px]" />
              <span className="text-[11px] font-bold tracking-wide">ROUTE A</span>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-800">Custom AI Generation</p>
              <p className="text-[10px] text-slate-400">
                {selectedExemplar ? 'Disabled — Route B is active. Select a template below to switch back to Route A.' : 'Define content, template, background style and scene prompt'}
              </p>
            </div>
          </div>

          {/* ── SECTION 1: Content Details ─────────────────────────────────── */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">1</span>
              <h3 className="text-sm font-bold text-slate-800">Content Details</h3>
              <span className="text-[10px] text-slate-400">— what text appears on the image</span>
            </div>

            <div className="space-y-4">
              {/* Headline */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                  <i className="fas fa-heading mr-1 text-violet-400" />
                  Headline <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={headline}
                  onChange={e => setHeadline(e.target.value)}
                  placeholder="e.g. Transform Your Business Today"
                  maxLength={80}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 transition-all"
                />
                <div className="flex justify-between mt-1">
                  <p className="text-[9px] text-slate-400">Displayed in bold on the template — keep it punchy</p>
                  <span className={`text-[9px] font-medium ${headline.length > 60 ? 'text-amber-500' : 'text-slate-400'}`}>{headline.length}/80</span>
                </div>
              </div>

              {/* Supporting text */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">
                  <i className="fas fa-align-left mr-1 text-violet-400" />
                  Supporting Text
                  <span className="ml-1 text-slate-300 font-normal normal-case">(optional)</span>
                </label>
                <textarea
                  value={subtext}
                  onChange={e => setSubtext(e.target.value)}
                  placeholder="e.g. Expert solutions tailored to your needs — book a free consultation today"
                  maxLength={320}
                  rows={4}
                  className="w-full px-3.5 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 transition-all resize-y min-h-[80px]"
                />
                <div className="flex justify-between mt-1">
                  <p className="text-[9px] text-slate-400">Supporting copy displayed on the image below the headline</p>
                  <span className={`text-[9px] font-medium ${subtext.length > 280 ? 'text-amber-500' : 'text-slate-400'}`}>{subtext.length}/320</span>
                </div>
              </div>

              {/* CTA */}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">
                  <i className="fas fa-hand-pointer mr-1 text-violet-400" />
                  Call-to-Action Button
                  <span className="ml-1 text-slate-300 font-normal normal-case">(optional)</span>
                </label>
                <div className="flex flex-wrap gap-2 mb-2.5">
                  {CTA_PRESETS.map(cta => (
                    <button
                      key={cta}
                      onClick={() => { setCtaText(cta); setCustomCta(false); }}
                      className={`px-3 py-1.5 text-[11px] font-semibold rounded-full border transition-all ${
                        ctaText === cta && !customCta
                          ? 'bg-violet-600 text-white border-violet-600 shadow-sm'
                          : 'border-slate-200 text-slate-600 hover:border-violet-300 hover:text-violet-600 bg-white'
                      }`}
                    >{cta}</button>
                  ))}
                  <button
                    onClick={() => { setCustomCta(true); setCtaText(''); }}
                    className={`px-3 py-1.5 text-[11px] font-semibold rounded-full border transition-all ${
                      customCta ? 'bg-violet-600 text-white border-violet-600' : 'border-dashed border-slate-300 text-slate-500 hover:border-violet-300 bg-white'
                    }`}
                  >
                    <i className="fas fa-plus text-[9px] mr-1" />Custom
                  </button>
                  {ctaText && (
                    <button
                      onClick={() => { setCtaText(''); setCustomCta(false); }}
                      className="px-3 py-1.5 text-[11px] font-semibold rounded-full border border-dashed border-slate-200 text-slate-400 hover:border-red-300 hover:text-red-500 bg-white transition-all"
                    >
                      <i className="fas fa-ban text-[9px] mr-1" />None
                    </button>
                  )}
                </div>
                {customCta && (
                  <input
                    type="text" value={ctaText}
                    onChange={e => setCtaText(e.target.value)}
                    placeholder="Type your CTA text…" maxLength={30} autoFocus
                    className="w-full px-3.5 py-2.5 border border-violet-300 rounded-xl text-sm text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-violet-200 transition-all"
                  />
                )}
                {ctaText && !customCta && (
                  <p className="text-[9px] text-slate-400 mt-1">
                    <i className="fas fa-info-circle mr-1 text-violet-300" />
                    Displayed as a pill button in your brand colour ({primaryColour})
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* ── Sections 2–4 dimmed when Route B active ───────────────────── */}
          <div className={`space-y-8 transition-opacity ${selectedExemplar ? 'opacity-30 pointer-events-none select-none' : ''}`}>

          {/* ── SECTION 2: Design Template ─────────────────────────────────── */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">2</span>
              <h3 className="text-sm font-bold text-slate-800">Design Template</h3>
              <span className="text-[10px] text-slate-400">— layout applied with exact brand colours</span>
            </div>

            {/* Colour swatch row — shows the brand colours being used */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[9px] text-slate-400 font-medium">Your brand:</span>
              {[primaryColour, secondaryColour, accentColour].map((c, i) => (
                <div key={i} className="flex items-center gap-1">
                  <div className="w-4 h-4 rounded-full border-2 border-white shadow" style={{ backgroundColor: c }} />
                  <span className="text-[9px] text-slate-500 font-mono">{c}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {TEMPLATES.map(tmpl => {
                const isActive = !selectedExemplar && selectedTemplate === tmpl.id;
                return (
                  <button
                    key={tmpl.id}
                    onClick={() => {
                      setSelectedTemplate(tmpl.id);
                      setSelectedExemplar(null); // deselect Route B
                      setPromptEnhanced(false);
                      setPrompt(buildSeedPrompt(day, businessData, styleChoice, tmpl.id));
                    }}
                    className={`relative flex flex-col rounded-xl border-2 overflow-hidden transition-all focus:outline-none text-left ${
                      isActive
                        ? 'border-violet-500 shadow-md shadow-violet-200/60'
                        : 'border-slate-200 hover:border-violet-300'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="w-full aspect-square bg-slate-100 overflow-hidden">
                      {thumbnails[tmpl.id] ? (
                        <img
                          src={thumbnails[tmpl.id]}
                          alt={tmpl.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                          <i className={`${tmpl.icon} text-slate-400 text-xl`} />
                        </div>
                      )}
                      {isActive && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-violet-600 flex items-center justify-center shadow-sm">
                          <i className="fas fa-check text-white text-[8px]" />
                        </div>
                      )}
                    </div>
                    {/* Label */}
                    <div className="px-2.5 py-2 bg-white">
                      <p className={`text-[11px] font-bold ${isActive ? 'text-violet-700' : 'text-slate-700'}`}>
                        {tmpl.name}
                      </p>
                      <p className="text-[9px] text-slate-400 leading-tight mt-0.5">{tmpl.desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            <p className="text-[9px] text-slate-400 mt-2.5 flex items-start gap-1">
              <i className="fas fa-info-circle text-violet-300 mt-0.5 flex-shrink-0" />
              <span>All brand colours ({primaryColour}) are applied programmatically — not left to AI. Thumbnails preview your actual brand palette.</span>
            </p>
          </section>

          {/* ── SECTION 3: Background Style + Aspect Ratio ─────────────────── */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">3</span>
              <h3 className="text-sm font-bold text-slate-800">Background Style & Size</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Background Style */}
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">AI Background Style</p>
                <div className="grid grid-cols-2 gap-2">
                  {STYLE_OPTIONS.map(s => (
                    <button
                      key={s.id}
                      onClick={() => {
                        setStyleChoice(s.id);
                        setPromptEnhanced(false);
                        setPrompt(buildSeedPrompt(day, businessData, s.id, selectedTemplate));
                      }}
                      className={`flex items-center gap-2 p-2.5 rounded-xl border-2 transition-all focus:outline-none ${
                        styleChoice === s.id
                          ? 'border-violet-500 bg-violet-50'
                          : 'border-slate-200 hover:border-violet-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${s.gradient} flex items-center justify-center shadow flex-shrink-0`}>
                        <i className={`${s.icon} text-white text-[10px]`} />
                      </div>
                      <div className="min-w-0">
                        <p className={`text-[11px] font-bold truncate ${styleChoice === s.id ? 'text-violet-700' : 'text-slate-700'}`}>{s.label}</p>
                        <p className="text-[9px] text-slate-400 leading-tight truncate">{s.desc}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Aspect Ratio */}
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">Aspect Ratio</p>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(ASPECT_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setAspectRatio(key)}
                      className={`px-2.5 py-2.5 text-[11px] font-semibold rounded-xl border-2 transition-all ${
                        aspectRatio === key
                          ? 'border-violet-500 bg-violet-50 text-violet-700'
                          : 'border-slate-200 text-slate-600 hover:border-violet-300 bg-white'
                      }`}
                    >{label}</button>
                  ))}
                </div>
                <p className="text-[9px] text-slate-400 mt-2">
                  <i className="fas fa-info-circle mr-1 text-violet-300" />
                  Auto-set for {day?.platform || 'your platform'}
                </p>
              </div>
            </div>
          </section>

          {/* ── SECTION 4: AI Prompt ───────────────────────────────────────── */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0">4</span>
                <h3 className="text-sm font-bold text-slate-800">AI Background Scene Prompt</h3>
                {promptEnhanced && !isLoadingPrompt && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-[9px] font-bold rounded-full">
                    <i className="fas fa-check-circle text-[8px]" />AI Enhanced
                  </span>
                )}
                {isLoadingPrompt && (
                  <span className="flex items-center gap-1 px-2 py-0.5 bg-violet-50 text-violet-600 text-[9px] font-medium rounded-full">
                    <i className="fas fa-spinner fa-spin text-[8px]" />Generating…
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={loadPrompt}
                  disabled={isLoadingPrompt}
                  className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold text-violet-600 bg-violet-50 hover:bg-violet-100 rounded-lg transition-all disabled:opacity-50 border border-violet-200"
                >
                  <i className={`fas ${isLoadingPrompt ? 'fa-spinner fa-spin' : 'fa-rotate'} text-[9px]`} />
                  Regenerate
                </button>
                <button
                  onClick={() => setEditingPrompt(e => !e)}
                  className={`flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-semibold rounded-lg transition-all border ${
                    editingPrompt
                      ? 'text-slate-900 bg-slate-100 border-slate-300'
                      : 'text-slate-600 bg-white hover:bg-slate-50 border-slate-200'
                  }`}
                >
                  <i className="fas fa-pencil text-[9px]" />
                  {editingPrompt ? 'Done' : 'Edit'}
                </button>
              </div>
            </div>

            {promptError && (
              <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-[10px] text-amber-700">
                <i className="fas fa-triangle-exclamation text-[9px]" />{promptError}
              </div>
            )}

            <div className="relative">
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                readOnly={!editingPrompt}
                rows={4}
                className={`w-full border rounded-xl px-4 py-3 text-[12px] text-slate-700 leading-relaxed resize-none transition-all focus:outline-none ${
                  editingPrompt
                    ? 'border-violet-400 bg-white focus:ring-2 focus:ring-violet-100'
                    : 'border-slate-200 bg-slate-50 cursor-default text-slate-600'
                }`}
                placeholder={isLoadingPrompt ? 'AI is crafting your background scene prompt…' : 'Prompt will appear here…'}
              />
              {isLoadingPrompt && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-50/80 rounded-xl">
                  <div className="flex flex-col items-center gap-2">
                    <div className="relative">
                      <div className="w-7 h-7 rounded-full border-2 border-violet-200 border-t-violet-600 animate-spin" />
                      <i className="fas fa-wand-magic-sparkles text-violet-500 text-[8px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </div>
                    <span className="text-[11px] text-violet-600 font-medium">Engineering your background scene prompt…</span>
                  </div>
                </div>
              )}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5 flex items-start gap-1">
              <i className="fas fa-info-circle text-violet-300 mt-0.5" />
              <span>This describes the <strong>background scene only</strong>. The selected template then composites all design elements (brand colours, fonts, logo, CTA) on top.</span>
            </p>
          </section>

          </div>{/* end Route A sections 2–4 wrapper */}

          {/* ══ ROUTE B — Style Reference Separator ══════════════════════════ */}
          {exemplarImages.length > 0 && (
            <>
              {/* OR divider */}
              <div className="relative flex items-center gap-4 py-2">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-200 to-slate-200" />
                <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 border border-slate-200 rounded-full shadow-sm flex-shrink-0">
                  <span className="text-xs font-bold text-slate-500 tracking-widest">OR</span>
                </div>
                <div className="flex-1 h-px bg-gradient-to-l from-transparent via-slate-200 to-slate-200" />
              </div>

              {/* Route B box */}
              <div className={`border-2 rounded-2xl overflow-hidden transition-all ${
                selectedExemplar
                  ? 'border-amber-400 shadow-lg shadow-amber-100/80'
                  : 'border-slate-200'
              }`}>
                {/* Route B header */}
                <div className={`flex items-center gap-3 px-4 py-3 ${
                  selectedExemplar ? 'bg-amber-50' : 'bg-slate-50'
                } border-b border-inherit`}>
                  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                    selectedExemplar ? 'bg-amber-500 text-white' : 'bg-slate-700 text-white'
                  }`}>
                    <i className="fas fa-image text-[11px]" />
                    <span className="text-[11px] font-bold tracking-wide">ROUTE B</span>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-800">Match Style from Reference</p>
                    <p className="text-[10px] text-slate-500">
                      {selectedExemplar
                        ? 'Style reference selected — AI will recreate a similar visual aesthetic'
                        : 'Pick one of your Business DNA reference images to guide the visual style'
                      }
                    </p>
                  </div>
                  {selectedExemplar && (
                    <span className="flex items-center gap-1 px-2.5 py-1 bg-amber-500 text-white text-[9px] font-bold rounded-full">
                      <i className="fas fa-check text-[8px]" />Active
                    </span>
                  )}
                </div>

                {/* Image grid */}
                <div className="p-4 space-y-4">
                  <p className="text-[10px] text-slate-500 leading-relaxed">
                    Select one image below. Our AI uses <strong>GPT-4 Vision</strong> to analyse its composition, lighting, colour palette and mood — then instructs <strong>gpt-image-1</strong> to recreate the same aesthetic for your post. Brand colours and template are layered on top.
                  </p>

                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {exemplarImages.map((url, i) => {
                      const fullUrl = resolveUrl(url);
                      const isSelected = selectedExemplar === url;
                      return (
                        <div key={i} className="space-y-1">
                          <div className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                            isSelected
                              ? 'border-amber-400 shadow-md shadow-amber-200/60'
                              : 'border-slate-200'
                          }`}>
                            <img
                              src={fullUrl}
                              alt={`Style ref ${i + 1}`}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const placeholder = document.createElement('div');
                                placeholder.className = 'w-full h-full bg-slate-100 flex items-center justify-center';
                                placeholder.innerHTML = '<i class="fas fa-image text-slate-300 text-2xl"></i>';
                                e.target.parentElement.appendChild(placeholder);
                              }}
                            />
                            {isSelected && (
                              <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                                <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center shadow-lg">
                                  <i className="fas fa-check text-white text-[10px]" />
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Select + View buttons */}
                          <div className="flex gap-1">
                            <button
                              onClick={() => setSelectedExemplar(isSelected ? null : url)}
                              className={`flex-1 py-1 text-[9px] font-bold rounded-lg transition-all ${
                                isSelected
                                  ? 'bg-amber-500 text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-amber-50 hover:text-amber-700'
                              }`}
                            >
                              {isSelected ? '✓ Selected' : 'Select'}
                            </button>
                            <button
                              onClick={() => setViewingExemplar(url)}
                              className="w-7 flex items-center justify-center bg-slate-100 text-slate-500 hover:bg-slate-200 rounded-lg transition-all"
                              title="View full image"
                            >
                              <i className="fas fa-expand text-[8px]" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {selectedExemplar && (
                    <div className="flex items-start gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                      <i className="fas fa-wand-magic-sparkles text-amber-500 text-sm mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-[11px] font-bold text-amber-800">Style reference locked in</p>
                        <p className="text-[10px] text-amber-700 mt-0.5">
                          When you generate, AI Vision will analyse this image and instruct the generator to recreate a similar composition, lighting and mood — adapted for your post topic and brand colours.
                        </p>
                        <button
                          onClick={() => setSelectedExemplar(null)}
                          className="mt-1.5 text-[9px] text-amber-500 hover:text-amber-700 underline"
                        >
                          Clear reference
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── Lightbox / View modal ───────────────────────────────────────── */}
          {viewingExemplar && (
            <div
              className="fixed inset-0 z-[99999] bg-black/80 flex items-center justify-center p-6"
              onClick={() => setViewingExemplar(null)}
            >
              <div
                className="relative max-w-xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl"
                onClick={e => e.stopPropagation()}
              >
                <img
                  src={resolveUrl(viewingExemplar)}
                  alt="Style reference"
                  className="w-full h-auto max-h-[65vh] object-contain bg-slate-50"
                />
                <div className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-wand-magic-sparkles text-amber-600 text-sm" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800">AI will recreate this visual aesthetic</p>
                      <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                        GPT-4 Vision analyses this image's composition, lighting, colour palette and mood. That style description is sent to the image generator, which creates a brand-new scene for your post topic while matching the same visual character — adapted with your brand colours on top.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setSelectedExemplar(viewingExemplar); setViewingExemplar(null); }}
                      className={`flex-1 py-2 text-[11px] font-bold rounded-xl transition-all ${
                        selectedExemplar === viewingExemplar
                          ? 'bg-amber-500 text-white'
                          : 'bg-amber-500 hover:bg-amber-600 text-white'
                      }`}
                    >
                      {selectedExemplar === viewingExemplar ? '✓ Already selected' : 'Use This Style'}
                    </button>
                    <button
                      onClick={() => setViewingExemplar(null)}
                      className="px-4 py-2 text-[11px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Error message ──────────────────────────────────────────────── */}
          {error && (
            <div className="flex items-center gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-[11px] text-red-600">
              <i className="fas fa-circle-exclamation flex-shrink-0" />
              {error}
            </div>
          )}

          {/* ── Generate Button ────────────────────────────────────────────── */}
          <div className="space-y-2 pb-4">
            <button
              onClick={handleGenerate}
              disabled={!headline.trim() || !prompt.trim() || isLoadingPrompt}
              className={`w-full py-4 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl flex items-center justify-center gap-3 transition-all hover:-translate-y-0.5 disabled:hover:translate-y-0 ${
                selectedExemplar
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-lg shadow-amber-200/60'
                  : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-purple-200/60'
              }`}
            >
              <i className="fas fa-wand-magic-sparkles" />
              {selectedExemplar ? (
                <>
                  <span>Generate Style-Matched Image</span>
                  <span className="opacity-70 text-[11px] font-normal">· Route B · exemplar aesthetic</span>
                </>
              ) : (
                <>
                  <span>Generate Final Image</span>
                  <span className="opacity-60 text-[11px] font-normal">· {TEMPLATES.find(t => t.id === selectedTemplate)?.name || 'Custom'} · ready to publish</span>
                </>
              )}
            </button>
            <p className="text-center text-[10px] text-slate-400">
              {selectedExemplar
                ? 'GPT-4 Vision analyses your reference → AI generates a matching aesthetic → raw image output'
                : 'AI generates background scene · Template engine applies exact brand colours · Single PNG ready to post'}
            </p>
            {!headline.trim() && (
              <p className="text-center text-[10px] text-amber-500">
                <i className="fas fa-triangle-exclamation mr-1" />
                Add a headline above to enable generation
              </p>
            )}
          </div>

        </div>
      )}
    </div>
  );
}
